import React, {useState, useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {RootStore} from '../../../redux/store'
import {SelectOption} from '../../../services/dashboardService'
import {getFilteredDepartmentsDropdownOptions} from '../../../utils/app-utils'
import {PayPeriodData} from '../../../models/enhancement/pay-period-data'
import {usePayPeriods} from './usePayPeriods'

export interface EnhancementReportSearch {
  departmentId?: number[] | null;
  teamId?: number[] | null;
  fromPayPeriod?: PayPeriodData | null;
  toPayPeriod?: PayPeriodData | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

interface UseEnhancementsFilterProps {
  resetForm: boolean;
  onFilterChange?: (search: EnhancementReportSearch) => void;
  onSearch?: (search: EnhancementReportSearch) => void;
}

export function useEnhancementsFilter({
                                        resetForm,
                                        onFilterChange,
                                        onSearch,
                                      }: UseEnhancementsFilterProps) {
  const {departments, allEmployees, allTeams} = useSelector(
    (state: RootStore) => state.appSettings
  )
  
  const {defaultFromPeriod, defaultToPeriod, isLoading: payPeriodsLoading} = usePayPeriods()

  const [fromPayPeriod, setFromPayPeriod] = useState<PayPeriodData | null>(null)
  const [toPayPeriod, setToPayPeriod] = useState<PayPeriodData | null>(null)
  const [selectedDepartments, setSelectedDepartments] = useState<SelectOption[]>([])
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([])
  const [selectedRequestTypes, setSelectedRequestTypes] = useState<string[]>([])
  const [teamList, setTeamList] = useState<SelectOption[]>([])

  const departmentsList = useMemo(
    () => getFilteredDepartmentsDropdownOptions(departments, allEmployees),
    [departments, allEmployees]
  )

  useEffect(() => {
    let filteredTeams = allTeams

    if (selectedDepartments?.length > 0) {
      const selectedDepartmentIds = selectedDepartments.map((dept) => dept.value)
      filteredTeams = allTeams.filter(
        (team) =>
          team.departmentId !== undefined && selectedDepartmentIds.includes(team.departmentId)
      )
    }

    const list: SelectOption[] = filteredTeams.map((item) => ({
      displayValue: item.teamName,
      value: item.teamId,
    }))

    setTeamList(list)

    setSelectedTeams([])
  }, [selectedDepartments, allTeams])


  const currentSearch = useMemo((): EnhancementReportSearch => {
    const departmentIds = selectedDepartments.length > 0
      ? selectedDepartments.map((dept) => dept.value)
      : null

    const teamIds = selectedTeams.length > 0
      ? selectedTeams.map((teams) => teams.value)
      : null

    const dateFrom = fromPayPeriod?.periodStart
      ? new Date(fromPayPeriod.periodStart).toISOString().split('T')[0]
      : null
    const dateTo = toPayPeriod?.periodEnd
      ? new Date(toPayPeriod.periodEnd).toISOString().split('T')[0]
      : null

    return {
      departmentId: departmentIds,
      teamId: teamIds,
      fromPayPeriod,
      toPayPeriod,
      dateFrom,
      dateTo,
    }
  }, [selectedDepartments, selectedTeams, fromPayPeriod, toPayPeriod])

  useEffect(() => {
    if (resetForm) {
      setSelectedDepartments([])
      setSelectedTeams([])
      setSelectedRequestTypes([])
      setFromPayPeriod(null)
      setToPayPeriod(null)
    }
  }, [resetForm])
  
  useEffect(() => {
    if (!payPeriodsLoading && defaultFromPeriod && defaultToPeriod) {
      if (fromPayPeriod === null) {
        setFromPayPeriod(defaultFromPeriod)
      }
      if (toPayPeriod === null) {
        setToPayPeriod(defaultToPeriod)
      }
    }
  }, [payPeriodsLoading, defaultFromPeriod, defaultToPeriod, fromPayPeriod, toPayPeriod])
  


  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (onSearch) {
      onSearch(currentSearch)
    }
  }

  return {
    currentSearch,
    selectedDepartments,
    setSelectedDepartments,
    selectedTeams,
    setSelectedTeams,
    fromPayPeriod,
    toPayPeriod,
    handleFromPayPeriodChange: setFromPayPeriod,
    handleToPayPeriodChange: setToPayPeriod,
    departmentsList,
    teamList,
    handleSubmit,
    selectedRequestTypes,
    setSelectedRequestTypes,
  }
}